import { Message, MessageBox } from 'element-ui'
import { getToken } from '@/utils/auth'
import { goToLogin } from '@/utils/relogin'

/** 与 request.js、login.js 保持一致，走同源 /dev-api 代理 */
const API_BASE = process.env.VUE_APP_BASE_API || '/dev-api'

/** 上传文件访问前缀，兼容生产域名配置 */
const FILE_BASE = (process.env.VUE_APP_FILE_BASE || `${API_BASE}/upload/`).replace(/\/?$/, '/')

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']

export const UPLOAD_IMAGE_URL = `${API_BASE}/upload/uploadImage`
export const UPLOAD_REGISTER_IMAGE_URL = `${API_BASE}/upload/registerImage`

/** 业务上传允许类型（不含 html） */
export const UPLOAD_ACCEPT = '.bmp,.gif,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rar,.zip,.pdf,.mp4,.avi,.rmvb'

/** 成果评价/非核心技术材料：仅文档类，不含图片 */
export const UPLOAD_DOCUMENT_ACCEPT = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rar,.zip,.pdf'

/** 注册证件照仅图片 */
export const UPLOAD_IMAGE_ACCEPT = '.bmp,.gif,.jpg,.jpeg,.png'

/**
 * 读取登录 token（兼容 Cookie / localStorage / sessionStorage）
 */
export function getStoredToken() {
  let token = getToken() || localStorage.getItem('token') || sessionStorage.getItem('token') || ''
  if (token.startsWith('Bearer ')) {
    token = token.slice(7)
  }
  return token.trim()
}

export function getUploadHeaders() {
  const token = getStoredToken()
  return { Authorization: 'Bearer ' + token }
}

/**
 * 上传前校验：无 token 时阻止上传并提示
 */
export function beforeUploadAuth(file) {
  if (!getStoredToken()) {
    Message.warning('请先登录后再上传文件')
    return false
  }
  return true
}

/**
 * 解析 el-upload 响应（兼容 R 与 AjaxResult 格式）
 */
export function parseUploadResponse(response) {
  if (!response) {
    return { ok: false, msg: '上传失败，服务器无响应' }
  }
  const code = response.code
  if (code === 401) {
    return { ok: false, expired: true, msg: response.msg || '登录状态已过期' }
  }
  if (code !== 200) {
    return { ok: false, msg: response.msg || '上传失败' }
  }
  const data = response.data || response
  return { ok: true, data }
}

/** 登录过期统一提示 */
export function promptReLogin() {
  MessageBox.confirm('登录状态已过期，请重新登录后再操作', '系统提示', {
    confirmButtonText: '重新登录',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    goToLogin()
  }).catch(() => {})
}

/**
 * 包装 on-success：自动处理 401 / 非 200
 */
export function wrapUploadSuccess(handler) {
  return function(response, file, fileList) {
    const result = parseUploadResponse(response)
    if (!result.ok) {
      if (result.expired) {
        promptReLogin()
      } else {
        Message.error(result.msg)
      }
      return
    }
    if (typeof handler === 'function') {
      handler(result.data, file, fileList, response)
    }
  }
}

/**
 * 将数据库中的文件路径解析为可访问的完整 URL
 * 兼容：完整 http(s) 地址、/dev-api/...、/profile/...、20260303/xxx.pdf 等格式
 */
export function resolveFileUrl(path) {
  if (path == null || path === '') {
    return ''
  }
  const val = String(path).trim()
  if (!val) {
    return ''
  }
  if (/^https?:\/\//i.test(val)) {
    return val
  }
  if (val.startsWith('//')) {
    return `${window.location.protocol}${val}`
  }
  if (val.startsWith('/dev-api/') || val.startsWith('/profile/') || val.startsWith('/upload/')) {
    return `${window.location.origin}${val}`
  }
  const normalized = val.replace(/^\/+/, '').replace(/^upload\//, '')
  if (/^https?:\/\//i.test(FILE_BASE)) {
    return `${FILE_BASE.replace(/\/?$/, '/')}${normalized}`
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const base = FILE_BASE.startsWith('/') ? `${origin}${FILE_BASE}` : FILE_BASE
  return `${base.replace(/\/?$/, '/')}${normalized}`
}

export function isImageFile(path) {
  if (!path) {
    return false
  }
  const ext = String(path).split('?')[0].split('.').pop()?.toLowerCase()
  return IMAGE_EXTENSIONS.includes(ext)
}

/** 从路径或 URL 中取文件名 */
export function getFileBaseName(path) {
  if (!path) {
    return ''
  }
  const normalized = String(path).split('?')[0]
  const parts = normalized.split(/[\\/]/)
  return parts[parts.length - 1] || normalized
}

/** 预览或下载已上传文件（图片走 imgShow，其他格式新窗口打开） */
export function openUploadedFile(path) {
  const url = resolveFileUrl(path)
  if (!url) {
    Message.warning('暂无可预览的文件')
    return
  }
  if (isImageFile(path)) {
    window.open(`/imgShow?url=${encodeURIComponent(url)}`, '_blank')
    return
  }
  window.open(url, '_blank')
}

/** 将存储路径转为 el-upload 可预览的 file-list 项 */
export function buildUploadFileList(pathsStr, namesStr) {
  const paths = String(pathsStr || '').split(',').map(item => item.trim()).filter(Boolean)
  if (!paths.length) {
    return []
  }
  const names = String(namesStr || '').split(',').map(item => item.trim())
  return paths.map((path, index) => {
    const rawName = names[index] || getFileBaseName(path)
    const displayName = getFileBaseName(rawName) || rawName
    return {
      name: displayName,
      url: resolveFileUrl(path),
      path,
      uid: String(index)
    }
  })
}

/** el-upload 点击文件名预览/下载 */
export function previewUploadFile(file) {
  if (!file) {
    return
  }
  openUploadedFile(file.path || file.url || file.name)
}
